// ============================================
// NRS.07 — Result Explanation Engine Service
// ============================================
// Spec: hlx-nrs-07-result-explanation-engine.md (LOCKED)
// Contract: Finding selection severity*0.7 + goalAlignment*0.3,
//           plain language (6th-grade), 3-range context, drip delivery
// ============================================

import type {
  RankedFinding,
  BiomarkerConcernMapping,
  PatientExplanation,
  ThreeRangeContext,
  DripSequence,
  ResultExplanationMetrics,
} from '../types/resultExplanationEngine';
import type { CaptainBiomarkerResult, CaptainResponse } from '../../shared/captainPipeline';

// ── Constants ──

const SEVERITY_WEIGHT = 0.7;
const GOAL_ALIGNMENT_WEIGHT = 0.3;
const FINDINGS_PER_DRIP = 3;
const MAX_FINDINGS_TO_SELECT = 5;

// ── Biomarker-Concern Mapping ──

const CONCERN_MAPPINGS: BiomarkerConcernMapping[] = [
  { biomarkerName: 'Ferritin', relatedConcerns: ['fatigue', 'tired', 'energy', 'exhaustion', 'brain fog'], relatedGoals: ['energy'] },
  { biomarkerName: 'Iron', relatedConcerns: ['fatigue', 'tired', 'energy', 'hair loss'], relatedGoals: ['energy'] },
  { biomarkerName: 'Vitamin D', relatedConcerns: ['fatigue', 'mood', 'depression', 'bone', 'immune', 'energy'], relatedGoals: ['energy', 'immune', 'longevity'] },
  { biomarkerName: 'B12', relatedConcerns: ['brain fog', 'fatigue', 'tingling', 'memory', 'cognition'], relatedGoals: ['cognition', 'energy'] },
  { biomarkerName: 'TSH', relatedConcerns: ['weight', 'fatigue', 'cold', 'hair', 'thyroid', 'metabolism'], relatedGoals: ['body_composition', 'energy'] },
  { biomarkerName: 'Testosterone', relatedConcerns: ['libido', 'muscle', 'energy', 'mood', 'sex drive'], relatedGoals: ['sexual_health', 'body_composition', 'energy'] },
  { biomarkerName: 'Estradiol', relatedConcerns: ['hot flashes', 'mood', 'bone', 'menopause', 'hormones'], relatedGoals: ['hormonal'] },
  { biomarkerName: 'Hemoglobin', relatedConcerns: ['fatigue', 'shortness of breath', 'pale', 'anemia'], relatedGoals: ['energy'] },
  { biomarkerName: 'Glucose', relatedConcerns: ['weight', 'diabetes', 'sugar', 'thirst', 'energy'], relatedGoals: ['body_composition', 'longevity'] },
  { biomarkerName: 'Total Cholesterol', relatedConcerns: ['heart', 'cardiovascular', 'cholesterol'], relatedGoals: ['cardiovascular', 'longevity'] },
  { biomarkerName: 'LDL', relatedConcerns: ['heart', 'cardiovascular', 'cholesterol'], relatedGoals: ['cardiovascular', 'longevity'] },
  { biomarkerName: 'HDL', relatedConcerns: ['heart', 'cardiovascular', 'cholesterol'], relatedGoals: ['cardiovascular', 'longevity'] },
  { biomarkerName: 'CRP', relatedConcerns: ['inflammation', 'pain', 'joint', 'autoimmune'], relatedGoals: ['immune', 'longevity'] },
  { biomarkerName: 'Magnesium', relatedConcerns: ['sleep', 'muscle', 'cramps', 'anxiety', 'stress'], relatedGoals: ['sleep', 'energy'] },
  { biomarkerName: 'Zinc', relatedConcerns: ['immune', 'skin', 'hair', 'taste'], relatedGoals: ['immune'] },
  { biomarkerName: 'DHEA-S', relatedConcerns: ['aging', 'energy', 'hormones', 'stress'], relatedGoals: ['longevity', 'hormonal'] },
  { biomarkerName: 'Cortisol', relatedConcerns: ['stress', 'sleep', 'weight', 'anxiety', 'fatigue'], relatedGoals: ['sleep', 'body_composition'] },
  { biomarkerName: 'Folate', relatedConcerns: ['fatigue', 'anemia', 'pregnancy', 'brain fog'], relatedGoals: ['energy', 'cognition'] },
  { biomarkerName: 'Vitamin A', relatedConcerns: ['skin', 'vision', 'immune'], relatedGoals: ['immune'] },
  { biomarkerName: 'Omega-3 Index', relatedConcerns: ['inflammation', 'heart', 'brain', 'joints'], relatedGoals: ['cardiovascular', 'cognition'] },
];

// ── Metrics State ──

let _metrics: ResultExplanationMetrics = {
  totalCalls: 0,
  successRate: 1,
  avgLatencyMs: 0,
  errorsByType: {},
  accuracyOnGoldStandard: 0,
};
let _totalLatency = 0;
let _successCount = 0;

// ── Finding Selection Algorithm ──

/**
 * Calculate severity score based on distance from optimal range.
 * Returns 0-1 where 1 = maximally suboptimal.
 */
export function calculateSeverityScore(biomarker: CaptainBiomarkerResult): number {
  const range = biomarker.functionalRange || biomarker.standardRange;
  if (!range) return 0;

  const mid = (range.min + range.max) / 2;
  const rangeWidth = range.max - range.min;
  if (rangeWidth === 0) return 0;

  const distance = Math.abs(biomarker.value - mid);
  const normalizedDistance = distance / rangeWidth;

  // Status-based weighting
  const statusWeights: Record<string, number> = {
    optimal: 0,
    suboptimal: 0.4,
    deficient: 0.8,
    high: 0.7,
  };
  const statusWeight = statusWeights[biomarker.status] ?? 0.3;

  return Math.min(1, (normalizedDistance * 0.5) + (statusWeight * 0.5));
}

/**
 * Calculate goal alignment score.
 * Returns 0-1 where 1 = perfectly aligned with patient goals.
 */
export function calculateGoalAlignment(
  biomarker: CaptainBiomarkerResult,
  patientGoals: string[],
  patientConcerns: string,
): number {
  if (patientGoals.length === 0 && !patientConcerns) return 0;

  const mapping = CONCERN_MAPPINGS.find(
    (m) => m.biomarkerName.toLowerCase() === biomarker.name.toLowerCase(),
  );
  if (!mapping) return 0;

  let score = 0;

  // Check goal matches
  for (const goal of patientGoals) {
    if (mapping.relatedGoals.includes(goal.toLowerCase())) {
      score += 0.5;
    }
  }

  // Check concern keyword matches
  if (patientConcerns) {
    const concernLower = patientConcerns.toLowerCase();
    for (const concern of mapping.relatedConcerns) {
      if (concernLower.includes(concern.toLowerCase())) {
        score += 0.3;
      }
    }
  }

  return Math.min(1, score);
}

/**
 * Rank all biomarkers by combined score.
 * Formula: severity * 0.7 + goalAlignment * 0.3
 */
export function rankFindings(
  biomarkers: CaptainBiomarkerResult[],
  patientGoals: string[],
  patientConcerns: string,
): RankedFinding[] {
  const rankings: RankedFinding[] = biomarkers.map((biomarker) => {
    const severityScore = calculateSeverityScore(biomarker);
    const goalAlignmentScore = calculateGoalAlignment(biomarker, patientGoals, patientConcerns);
    const combinedScore = (severityScore * SEVERITY_WEIGHT) + (goalAlignmentScore * GOAL_ALIGNMENT_WEIGHT);

    const mapping = CONCERN_MAPPINGS.find(
      (m) => m.biomarkerName.toLowerCase() === biomarker.name.toLowerCase(),
    );
    const alignedGoals = mapping
      ? patientGoals.filter((g) => mapping.relatedGoals.includes(g.toLowerCase()))
      : [];

    return {
      biomarker,
      severityScore,
      goalAlignmentScore,
      combinedScore,
      rank: 0, // Set after sorting
      alignedGoals,
    };
  });

  // Sort by combined score descending
  rankings.sort((a, b) => b.combinedScore - a.combinedScore);

  // Assign ranks
  rankings.forEach((r, i) => { r.rank = i + 1; });

  return rankings;
}

/**
 * Select top findings for explanation.
 */
export function selectFindings(
  rankings: RankedFinding[],
  maxFindings: number = MAX_FINDINGS_TO_SELECT,
): RankedFinding[] {
  return rankings.slice(0, maxFindings);
}

// ── Plain Language Translation ──

/**
 * Generate a plain language explanation for a biomarker finding.
 * Target: 6th-grade reading level. No jargon. No diagnostic language.
 * XMO.09 D#3 c5: If correctedBiomarkers set includes this biomarker,
 * tags the explanation with correction source for transparency.
 */
export function generateExplanation(
  finding: RankedFinding,
  _patientConcerns: string,
  correctedBiomarkers?: Set<string>,
): PatientExplanation {
  const biomarker = finding.biomarker;

  // 3-range context
  const rangeContext: ThreeRangeContext = {
    conventionalRange: `${biomarker.standardRange.min}-${biomarker.standardRange.max} ${biomarker.unit}`,
    functionalRange: biomarker.functionalRange
      ? `${biomarker.functionalRange.min}-${biomarker.functionalRange.max} ${biomarker.unit}`
      : 'not available',
    patientStatus: biomarker.status,
  };

  // Build explanation (6th-grade reading level)
  let explanation = '';
  const isOptimal = biomarker.status === 'optimal';

  if (isOptimal) {
    explanation = `Your ${biomarker.name} is at ${biomarker.value} ${biomarker.unit}, which is right where we want it. Nice.`;
  } else {
    // Link to concern if possible
    const concernLink = finding.alignedGoals.length > 0
      ? ` This connects to what you mentioned about ${finding.alignedGoals[0]}.`
      : '';

    const statusDescription = biomarker.status === 'suboptimal'
      ? 'a bit lower than we\'d like to see'
      : biomarker.status === 'deficient'
        ? 'lower than it should be'
        : biomarker.status === 'high'
          ? 'higher than we\'d like to see'
          : 'not quite where we want it';

    explanation = `Your ${biomarker.name} is at ${biomarker.value} ${biomarker.unit} — that's ${statusDescription}. Most labs would call this normal, but for someone focused on feeling their best, we'd want it ${biomarker.status === 'high' ? 'lower' : 'higher'}.${concernLink}`;
  }

  // XMO.09 D#3 c5: Tag correction source for transparency
  const isCorrected = correctedBiomarkers?.has(biomarker.name.toLowerCase());
  const correctionSource = isCorrected ? 'Updated result from lab review' : undefined;
  if (isCorrected) {
    explanation = `[${correctionSource}] ${explanation}`;
  }

  return {
    biomarkerName: biomarker.name,
    explanation,
    valueDisplay: `${biomarker.value} ${biomarker.unit}`,
    rangeContext,
    linkedGoal: finding.alignedGoals[0],
    isOptimal,
    correctionSource,
  };
}

// ── Drip Delivery ──

/**
 * Create a drip delivery sequence from selected findings.
 * Delivers 2-3 findings at a time.
 * XMO.09 D#3 c5: correctedBiomarkers set tags corrected findings for transparency.
 */
export function createDripSequence(
  findings: RankedFinding[],
  patientConcerns: string,
  correctedBiomarkers?: Set<string>,
): DripSequence {
  const explanations = findings.map((f) => generateExplanation(f, patientConcerns, correctedBiomarkers));

  return {
    findings: explanations,
    findingsPerDrip: Math.min(FINDINGS_PER_DRIP, explanations.length),
    currentDripIndex: 0,
    complete: false,
    emitShowReport: false,
  };
}

/**
 * Get the next batch of findings from the drip sequence.
 */
export function getNextDrip(sequence: DripSequence): {
  findings: PatientExplanation[];
  isLast: boolean;
  updatedSequence: DripSequence;
} {
  const start = sequence.currentDripIndex;
  const end = Math.min(start + sequence.findingsPerDrip, sequence.findings.length);
  const findings = sequence.findings.slice(start, end);
  const isLast = end >= sequence.findings.length;

  return {
    findings,
    isLast,
    updatedSequence: {
      ...sequence,
      currentDripIndex: end,
      complete: isLast,
      emitShowReport: isLast,
    },
  };
}

// ── Full Pipeline ──

/**
 * Run the full explanation pipeline.
 */
export function explainResults(
  captainResponse: CaptainResponse,
  patientGoals: string[],
  patientConcerns: string,
): DripSequence {
  const startTime = Date.now();
  _metrics.totalCalls++;

  // Step 1: Rank all findings
  const rankings = rankFindings(captainResponse.biomarkers, patientGoals, patientConcerns);

  // Step 2: Select top findings
  const selected = selectFindings(rankings);

  // Step 3: Create drip sequence
  const sequence = createDripSequence(selected, patientConcerns);

  _successCount++;
  _totalLatency += Date.now() - startTime;
  return sequence;
}

// ── Present Lab Results (NRS.01 D#4) ──

export interface PresentLabResultsOptions {
  /** When true, findings include completeness indicator for partial extractions */
  isPartial?: boolean;
}

/**
 * NRS.01 D#4: Present lab results to the patient via the explanation engine.
 * Wraps explainResults() with isPartial support — partial results get a
 * completeness indicator prepended to the first finding's explanation.
 */
export function presentLabResults(
  captainResponse: CaptainResponse,
  patientGoals: string[],
  patientConcerns: string,
  options?: PresentLabResultsOptions,
): DripSequence {
  const sequence = explainResults(captainResponse, patientGoals, patientConcerns);

  // For partial results, prepend completeness indicator to first finding
  if (options?.isPartial && sequence.findings.length > 0) {
    const totalBiomarkers = captainResponse.biomarkers.length;
    sequence.findings[0] = {
      ...sequence.findings[0],
      explanation: `[Partial results: ${totalBiomarkers} of your biomarkers were successfully read. Some values couldn't be extracted from your labs — the ones below are what we could confirm.]\n\n${sequence.findings[0].explanation}`,
    };
  }

  return sequence;
}

// ── XMO.09 D#3 c4: Re-run explanation pipeline on corrected values ──

/**
 * XMO.09 D#3 c4: Re-run the explanation pipeline when corrections arrive mid-conversation.
 * Identical to explainResults() but tracks which biomarkers were corrected
 * so explanations include the "Updated result from lab review" tag (c5).
 */
export function explainCorrectedResults(
  correctedCaptainResponse: CaptainResponse,
  patientGoals: string[],
  patientConcerns: string,
  correctedBiomarkerNames: string[],
): DripSequence {
  const startTime = Date.now();
  _metrics.totalCalls++;

  // Build set of corrected biomarker names (lowercase for case-insensitive match)
  const correctedSet = new Set(correctedBiomarkerNames.map((n) => n.toLowerCase()));

  // Step 1: Rank all findings (same algorithm as explainResults)
  const rankings = rankFindings(correctedCaptainResponse.biomarkers, patientGoals, patientConcerns);

  // Step 2: Select top findings
  const selected = selectFindings(rankings);

  // Step 3: Create drip sequence with correction tagging
  const sequence = createDripSequence(selected, patientConcerns, correctedSet);

  _successCount++;
  _totalLatency += Date.now() - startTime;
  return sequence;
}

// ── Metrics ──

export function getMetrics(): ResultExplanationMetrics {
  return {
    ..._metrics,
    successRate: _metrics.totalCalls > 0 ? _successCount / _metrics.totalCalls : 1,
    avgLatencyMs: _metrics.totalCalls > 0 ? _totalLatency / _metrics.totalCalls : 0,
  };
}
